import { decryptSharedItem, encryptSharedItem } from '@heybesafe/shared';
import type { VaultItem, EncryptedVaultItem } from '@heybesafe/shared';

/** The three record fields produced by encrypting a member's display name. */
export interface EncryptedMemberName {
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
}

/** The subset of `OrgMember` this module reads. Declared structurally so tests
 *  and callers need not build a whole member. */
export interface MemberNameBlob {
  encryptedName?: string;
  encryptedNameIv?: string;
  encryptedNameEphemeralPublicKey?: string;
}

/**
 * Encrypt a member's display name to the family public key.
 *
 * Uses the **same `share-item-v1` scheme** as shared vault items and Binder
 * category names (see `categoryName.ts`) by presenting `{ name }` as a
 * `VaultItem`; only the `ciphertext` / `iv` / `ephemeralPublicKey` outputs are
 * kept. A fresh ephemeral keypair per call means two encryptions of the same
 * name never produce the same bytes.
 */
export async function encryptMemberName(
  name: string,
  familyPublicKey: Uint8Array,
): Promise<EncryptedMemberName> {
  const enc = await encryptSharedItem({ name } as unknown as VaultItem, familyPublicKey);
  return {
    encryptedName: enc.ciphertext,
    encryptedNameIv: enc.iv,
    ephemeralPublicKey: enc.ephemeralPublicKey!,
  };
}

/**
 * Decrypt a member's display name. Throws when the blob is incomplete or the
 * key is wrong — callers fall back to the member's email rather than surfacing
 * the error (see `useMemberNames`).
 */
export async function decryptMemberName(
  member: MemberNameBlob,
  familyPrivateKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<string> {
  const decoded = (await decryptSharedItem(
    {
      id: 'member-name',
      ciphertext: member.encryptedName ?? '',
      iv: member.encryptedNameIv ?? '',
      clientUpdatedAt: '',
      ephemeralPublicKey: member.encryptedNameEphemeralPublicKey,
    } as EncryptedVaultItem,
    familyPrivateKey,
    familyPublicKey,
  )) as unknown as { name?: unknown };
  if (typeof decoded.name !== 'string') throw new Error('member name blob has no name');
  return decoded.name;
}
